import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Users, CalendarHeart, UserCircle, LogOut, Eye, X, BookOpen, ShieldCheck, Briefcase, GraduationCap } from "lucide-react";
import Footer from "@/components/Footer";
import { InstallPWA, InstallNudge } from "@/components/InstallPWA";
import ThemeMenu from "@/components/ThemeMenu";
import { useBranding } from "@/lib/branding";

const TABS = [
  { to: "/experience", label: "Experience", icon: Sparkles, end: true, testid: "atab-experience" },
  { to: "/experience/directory", label: "Class Directory", icon: Users, testid: "atab-directory" },
  { to: "/experience/careers", label: "Career Network", icon: Briefcase, testid: "atab-careers" },
  { to: "/experience/faculty", label: "Faculty & Staff", icon: GraduationCap, testid: "atab-faculty" },
  { to: "/experience/events", label: "Events & Photos", icon: CalendarHeart, testid: "atab-events" },
  { to: "/experience/tower", label: "Tower Magazine", icon: BookOpen, testid: "atab-tower" },
  { to: "/me", label: "My Profile", icon: UserCircle, testid: "atab-profile" },
];

export default function AlumniLayout({ children, wide }) {
  const { user, logout, stopImpersonation, isClassAgent } = useAuth();
  const b = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  // Remember the last alumni tab the (real, non-impersonated) alumnus was on,
  // so the next sign-in lands them right back where they left off.
  useEffect(() => {
    if (user?.role === "alumnus" && !user?.impersonated) {
      localStorage.setItem("ud_alumni_last_tab", location.pathname + location.search);
    }
  }, [location, user]);

  const exitPreview = async () => {
    await stopImpersonation();
    const back = localStorage.getItem("ud_emulate_return");
    localStorage.removeItem("ud_emulate_return");
    navigate(back || "/");
  };

  return (
    <div className="min-h-screen bg-slate-300/60">
      {user?.impersonated && (
        <div data-testid="impersonation-banner" className="bg-accent text-secondary text-sm font-medium">
          <div className="max-w-6xl mx-auto px-5 py-2 flex items-center justify-between gap-3">
            <span className="flex items-center gap-2"><Eye className="h-4 w-4" /> Admin preview — you are viewing as <b>{user?.name}</b></span>
            <button data-testid="exit-preview" onClick={exitPreview} className="flex items-center gap-1.5 px-3 py-1 rounded bg-secondary text-white text-xs font-medium hover:bg-secondary/90"><X className="h-3.5 w-3.5" /> Exit preview</button>
          </div>
        </div>
      )}
      <header className="bg-secondary text-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <a href={b.university_url || "#"} target="_blank" rel="noopener noreferrer" title={`Visit ${b.university_name}`} data-testid="ud-seal-link" className="h-10 w-10 rounded-full bg-white flex items-center justify-center p-0.5 shrink-0 transition-transform hover:scale-105">{b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}</a>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}</div>
                <div className="font-serif text-lg leading-none mt-0.5">Alumni Experience</div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <ThemeMenu />
              <InstallPWA />
              {isClassAgent && (
                <span data-testid="class-agent-badge" title="You are a Class Agent" className="inline-flex items-center gap-1.5 rounded-full bg-accent/20 text-accent border border-accent/40 px-2.5 py-1 text-xs font-semibold">
                  <ShieldCheck className="h-3.5 w-3.5" /> <span className="hidden xs:inline">Class Agent</span>
                </span>
              )}
              <span className="hidden sm:block text-sm text-white/70">{user?.name}</span>
              <button data-testid="alumni-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white"><LogOut className="h-4 w-4" /> <span className="hidden xs:inline">Sign out</span></button>
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto -mb-px">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
                  className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${isActive ? "border-accent text-white" : "border-transparent text-white/60 hover:text-white"}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>
      <main className={`${wide ? "max-w-6xl" : "max-w-4xl"} mx-auto px-4 sm:px-5 py-6 sm:py-8 fade-up`}>{children}</main>
      {!user?.impersonated && <InstallNudge />}
      <Footer />
    </div>
  );
}
